import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../component/Navbar';
import './CancelTest.css';

const API_BASE_URL = 'https://apiv2.labridge.in';

const getErrorMessage = (error, fallback) =>
  error.response?.data?.message || error.response?.data?.error || fallback;

const formatDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })
    : '-';
};

function CancelTest() {
  const { orderId: routeOrderId } = useParams();
  const navigate = useNavigate();
  const [orderId, setOrderId] = useState(routeOrderId || '');
  const [order, setOrder] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const cancellableItems = useMemo(
    () => (order?.orderItems || []).filter((item) => item.canCancel && !item.isCancelled),
    [order]
  );

  const loadOrder = async (id = orderId) => {
    const trimmedOrderId = id.trim();
    if (!trimmedOrderId) {
      setOrder(null);
      setError('Enter an order ID to view its tests.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/bookTestB2b/cancel-booking-details/${encodeURIComponent(trimmedOrderId)}`
      );

      if (!response.data?.success || !response.data?.data) {
        throw new Error(response.data?.message || 'Could not load cancellation details.');
      }

      setOrder(response.data.data);
      setSelectedItemIds([]);
      setReason('');
    } catch (requestError) {
      setOrder(null);
      setSelectedItemIds([]);
      setError(getErrorMessage(requestError, requestError.message || 'Unable to load this order.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const adminData = JSON.parse(localStorage.getItem('loginData'));
    if (!adminData || !Array.isArray(adminData.access) || !adminData.access.includes('3')) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (routeOrderId) {
      setOrderId(routeOrderId);
      loadOrder(routeOrderId);
    }
    // The route parameter is the only intended automatic lookup trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOrderId]);

  const toggleItem = (itemId) => {
    setSelectedItemIds((currentIds) =>
      currentIds.includes(itemId)
        ? currentIds.filter((id) => id !== itemId)
        : [...currentIds, itemId]
    );
  };

  const toggleAll = () => {
    const cancellableItemIds = cancellableItems.map((item) => item.orderItemId);
    setSelectedItemIds(
      selectedItemIds.length === cancellableItemIds.length ? [] : cancellableItemIds
    );
  };

  const cancelTests = async () => {
    if (!order) return;
    if (!selectedItemIds.length) {
      setError('Select at least one test to cancel.');
      return;
    }
    if (!reason.trim()) {
      setError('Enter a cancellation reason.');
      return;
    }

    const selectedTestCount = selectedItemIds.length;
    if (!window.confirm(`Cancel ${selectedTestCount} selected test${selectedTestCount > 1 ? 's' : ''}?`)) {
      return;
    }

    setCancelling(true);
    setError('');
    setSuccess(null);
    try {
      const response = await axios.post(`${API_BASE_URL}/bookTestB2b/cancel-booking-tests`, {
        orderId: order.orderId || orderId.trim(),
        orderItemIds: selectedItemIds,
        reason: reason.trim(),
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Tests could not be cancelled.');
      }

      await loadOrder(order.orderId || orderId.trim());
      setSuccess(response.data);
    } catch (requestError) {
      setError(getErrorMessage(requestError, requestError.message || 'Unable to cancel selected tests.'));
    } finally {
      setCancelling(false);
    }
  };

  const allCancellableSelected =
    cancellableItems.length > 0 && selectedItemIds.length === cancellableItems.length;

  return (
    <div className="wrapper">
      <Navbar />
      <main className="container mt-5 cancel-test-page" id="content">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1 className="mb-1">Cancel Booking Tests</h1>
            <p className="text-muted mb-0">Select only the tests that should be cancelled.</p>
          </div>
        </div>

        <form
          className="card mb-4"
          onSubmit={(event) => {
            event.preventDefault();
            loadOrder();
          }}
        >
          <div className="card-body d-flex gap-2 flex-wrap">
            <label className="visually-hidden" htmlFor="order-id">Order ID</label>
            <input
              id="order-id"
              type="text"
              className="form-control cancel-order-id-input"
              placeholder="Enter order ID"
              value={orderId}
              onChange={(event) => setOrderId(event.target.value)}
              disabled={loading || cancelling}
            />
            <button type="submit" className="btn btn-primary" disabled={loading || cancelling}>
              {loading ? 'Loading…' : 'Find order'}
            </button>
          </div>
        </form>

        {error && <div className="alert alert-danger" role="alert">{error}</div>}

        {success && (
          <div className="alert alert-success" role="status">
            <strong>{success.message || 'Tests cancelled successfully.'}</strong>
            {success.refundAmount !== undefined && <> Refund amount: {formatAmount(success.refundAmount)}.</>}
            {success.orderStatus && <> Order status: {success.orderStatus}.</>}
          </div>
        )}

        {order && (
          <>
            <section className="card mb-4" aria-labelledby="patient-information-heading">
              <div className="card-body">
                <h2 id="patient-information-heading" className="h4 mb-3">Booking details</h2>
                <div className="row g-3">
                  <Detail label="Order ID" value={order.orderId} />
                  <Detail label="Patient" value={order.patientName} />
                  <Detail label="Age / Gender" value={[order.age, order.gender].filter(Boolean).join(' · ')} />
                  <Detail label="Mobile" value={order.mobile} />
                  <Detail label="Doctor" value={order.doctorName} />
                  <Detail label="Lab" value={[order.labhpl, order.lab].filter(Boolean).join(' · ')} />
                  <Detail label="Order status" value={order.status} />
                  <Detail label="Ordered on" value={formatDate(order.dateOrdered)} />
                  <Detail label="Total price" value={formatAmount(order.totalprice)} />
                </div>
              </div>
            </section>

            <section className="card" aria-labelledby="tests-heading">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h2 id="tests-heading" className="h4 mb-0">Tests</h2>
                  {cancellableItems.length > 0 && (
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={toggleAll} disabled={cancelling}>
                      {allCancellableSelected ? 'Clear selection' : 'Select all cancellable'}
                    </button>
                  )}
                </div>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-4">
                    <thead>
                      <tr>
                        <th scope="col">Select</th>
                        <th scope="col">Test</th>
                        <th scope="col">Lab</th>
                        <th scope="col">Amount</th>
                        <th scope="col">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.orderItems || []).map((item) => {
                        const isCancellable = item.canCancel && !item.isCancelled;
                        const isSelected = selectedItemIds.includes(item.orderItemId);
                        const status = item.isCancelled
                          ? 'Cancelled'
                          : item.status || (isCancellable ? 'Available to cancel' : item.blockReason || 'Cannot cancel');

                        return (
                          <tr key={item.orderItemId} className={!isCancellable ? 'cancel-test-disabled-row' : ''}>
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={isSelected}
                                disabled={!isCancellable || cancelling}
                                onChange={() => toggleItem(item.orderItemId)}
                                aria-label={`Select ${item.testName} for cancellation`}
                              />
                            </td>
                            <td>
                              <div>{item.testName || '-'}</div>
                              <small className="text-muted">{item.orderItemId}</small>
                            </td>
                            <td>{item.labCode || '-'}</td>
                            <td>{formatAmount(item.b2bAmount)}</td>
                            <td>
                              <span className={isCancellable ? 'text-success' : 'text-muted'}>{status}</span>
                              {!isCancellable && item.blockReason && !item.isCancelled && (
                                <small className="d-block text-muted">{item.blockReason}</small>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {!order.orderItems?.length && <p className="text-muted">No tests were found for this order.</p>}

                {cancellableItems.length > 0 && (
                  <div className="border-top pt-3">
                    <label className="form-label" htmlFor="cancellation-reason">Cancellation reason</label>
                    <textarea
                      id="cancellation-reason"
                      className="form-control mb-3"
                      rows="3"
                      maxLength="500"
                      placeholder="For example: Patient requested cancellation"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      disabled={cancelling}
                      required
                    />
                    <button type="button" className="btn btn-danger" onClick={cancelTests} disabled={cancelling || !selectedItemIds.length}>
                      {cancelling ? 'Cancelling…' : `Cancel selected tests${selectedItemIds.length ? ` (${selectedItemIds.length})` : ''}`}
                    </button>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="col-12 col-sm-6 col-lg-4">
      <div className="cancel-detail-label">{label}</div>
      <div>{value || '-'}</div>
    </div>
  );
}

export default CancelTest;
